import type { FC, FormEvent, ReactNode } from 'react'
import type { SearchProduct } from '@/pages/api/products'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Search, X } from 'lucide-react'
import { formatIDR } from '@/lib/format'
import { loadProductFeed } from '@/lib/product-feed'
import { useShop } from '@/components/providers/shop-provider'
import { Sheet } from '@/components/generals/sheet'
import { ProductImage } from '@/components/shop/product-image'
import { labelName } from '@/constants/site'

const matches = (product: SearchProduct, terms: Array<string>) => {
    const haystack = `${product.name} ${product.tagline ?? ''} ${product.category?.name ?? ''} ${product.badge ?? ''} ${product.labels.map(labelName).join(' ')}`.toLowerCase()

    return terms.every(term => haystack.includes(term))
}

const SUGGESTIONS = ['Beruang', 'Kelinci', 'Gantungan kunci', 'Unicorn', 'Custom']

export const SearchDialog: FC = (): ReactNode => {
    const router = useRouter()
    const { isSearchOpen, closeSearch } = useShop()
    const [query, setQuery] = useState('')
    const [products, setProducts] = useState<Array<SearchProduct>>([])
    const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

    // Load the catalogue the first time search opens (and retry on a later open if it failed).
    useEffect(() => {
        if (!isSearchOpen) return

        let cancelled = false

        setState(current => (current === 'ready' ? current : 'loading'))
        loadProductFeed()
            .then(result => {
                if (cancelled) return

                setProducts(result)
                setState('ready')
            })
            .catch(() => {
                if (!cancelled) setState('error')
            })

        return () => {
            cancelled = true
        }
    }, [isSearchOpen])

    const results = useMemo(() => {
        const terms = query.toLowerCase().split(/\s+/).filter(Boolean)

        if (!terms.length) return products.slice(0, 6)

        return products.filter(product => matches(product, terms)).slice(0, 8)
    }, [products, query])

    const submit = (event: FormEvent) => {
        event.preventDefault()
        closeSearch()
        router.push(query.trim() ? `/shop?q=${encodeURIComponent(query.trim())}` : '/shop')
    }

    return (
        <Sheet open={isSearchOpen} onClose={closeSearch} labelledBy='search-title' side='top'>
            <div className='mx-auto w-full max-w-3xl px-5 pb-8 pt-6 sm:px-8'>
                <div className='flex items-center justify-between'>
                    <h2 id='search-title' className='font-heading text-2xl text-cocoa-900'>
                        Cari Produk
                    </h2>
                    <button
                        type='button'
                        onClick={closeSearch}
                        aria-label='Tutup pencarian'
                        className='grid size-10 place-items-center rounded-full text-cocoa-800 transition hover:bg-muted'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                <form onSubmit={submit} role='search' className='relative mt-4'>
                    <label htmlFor='search-input' className='sr-only'>
                        Cari di koleksi
                    </label>
                    <Search className='pointer-events-none absolute left-5 top-1/2 size-5 -translate-y-1/2 text-cocoa-500' />
                    <input
                        id='search-input'
                        data-autofocus
                        type='search'
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder='Beruang, kelinci, gantungan kunci...'
                        autoComplete='off'
                        className='h-14 w-full rounded-full border border-input bg-white pl-14 pr-28 text-base outline-none transition focus:border-caramel-400 focus:ring-4 focus:ring-caramel-300/30'
                    />
                    <button
                        type='submit'
                        className='absolute right-2 top-1/2 inline-flex h-10 -translate-y-1/2 items-center gap-1.5 rounded-full bg-cocoa-800 px-4 text-sm font-semibold text-cream-50 transition hover:bg-cocoa-700'
                    >
                        Cari
                        <ArrowRight className='size-4' />
                    </button>
                </form>

                {!query && (
                    <div className='mt-4 flex flex-wrap gap-2'>
                        {SUGGESTIONS.map(suggestion => (
                            <button
                                key={suggestion}
                                type='button'
                                onClick={() => setQuery(suggestion)}
                                className='rounded-full border border-border bg-white px-3.5 py-1.5 text-xs font-semibold text-cocoa-700 transition hover:border-blush-300 hover:bg-blush-50'
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                )}

                <div className='mt-6' aria-live='polite'>
                    {state === 'loading' && (
                        <p className='py-6 text-center text-sm text-muted-foreground'>
                            Mengumpulkan boneka-boneka...
                        </p>
                    )}
                    {state === 'error' && (
                        <p className='py-6 text-center text-sm text-muted-foreground'>
                            Pencarian sedang tidak bisa dipakai. Coba lagi sebentar, atau lihat semua produk.
                        </p>
                    )}
                    {state === 'ready' && results.length === 0 && (
                        <p className='py-6 text-center text-sm text-muted-foreground'>
                            Belum ada produk yang cocok. Coba kata lain, atau pesan boneka custom.
                        </p>
                    )}
                    {state === 'ready' && results.length > 0 && (
                        <>
                            <p className='mb-3 text-xs font-bold uppercase tracking-[0.2em] text-cocoa-500'>
                                {query ? 'Hasil pencarian' : 'Pilihan untukmu'}
                            </p>
                            <ul className='grid gap-2 sm:grid-cols-2'>
                                {results.map(product => (
                                    <li key={product.id}>
                                        <Link
                                            href={`/products/${product.slug}`}
                                            onClick={closeSearch}
                                            className='flex items-center gap-4 rounded-2xl border border-transparent p-2 transition hover:border-border hover:bg-white'
                                        >
                                            <ProductImage
                                                src={product.image}
                                                alt={product.imageAlt ?? product.name}
                                                designKey={product.designKey}
                                                mainColor={product.colorHex}
                                                accentColor={product.accentColor}
                                                className='aspect-square w-16 shrink-0 rounded-xl'
                                            />
                                            <span className='min-w-0'>
                                                <span className='block truncate font-heading text-lg text-cocoa-900'>
                                                    <span data-mdlfy-cms={`products:${product.id}:name`}>
                                                        {product.name}
                                                    </span>
                                                </span>
                                                <span className='mt-0.5 flex items-center gap-2 text-xs text-muted-foreground'>
                                                    {product.category && (
                                                        <span data-mdlfy-cms={`categories:${product.category.id}:name`}>
                                                            {product.category.name}
                                                        </span>
                                                    )}
                                                    {product.category && <span aria-hidden='true'>·</span>}
                                                    <span className='font-semibold text-cocoa-700'>
                                                        {formatIDR(product.price)}
                                                    </span>
                                                    {!product.madeToOrder && product.stock <= 0 && (
                                                        <span className='font-semibold text-blush-600'>Stok Habis</span>
                                                    )}
                                                </span>
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </Sheet>
    )
}
